if (!customElements.get('tabbed-header')) {
    class TabbedHeader extends HTMLElement {
        constructor() {
            super();
            this.nav = this.querySelector('.tabbed-header__nav');
            this.navList = this.querySelector('.tabbed-header__nav-list');
            this.navLinks = this.querySelectorAll('.tabbed-header__nav-link');
            this.currentUrl = window.location.pathname;
            this.updateActiveStates = this.updateActiveStates.bind(this);
            this.scrollToActiveTab = this.scrollToActiveTab.bind(this);

            this.init();
        }

        connectedCallback() {
            // Re-initialize if element is reconnected (e.g., after preload-links navigation)
            if (this.navLinks.length === 0) {
                this.nav = this.querySelector('.tabbed-header__nav');
                this.navList = this.querySelector('.tabbed-header__nav-list');
                this.navLinks = this.querySelectorAll('.tabbed-header__nav-link');
            }
            this.init();
        }

        init() {
            this.updateActiveStates();
            this.setupEventListeners();
            // Delay scroll to ensure layout is complete
            requestAnimationFrame(() => {
                this.scrollToActiveTab();
            });
        }

        /**
         * Updates active states based on current URL
         */
        updateActiveStates() {
            const currentPath = window.location.pathname;

            this.navLinks.forEach((link) => {
                try {
                    const linkUrl = new URL(link.href, window.location.origin);
                    const linkPath = linkUrl.pathname;

                    // Remove active state
                    link.classList.remove('tabbed-header__nav-link--selected');
                    link.removeAttribute('aria-current');

                    // Check if this link matches current page
                    if (linkPath === currentPath) {
                        link.classList.add('tabbed-header__nav-link--selected');
                        link.setAttribute('aria-current', 'page');
                    } else if (currentPath !== '/' && linkPath !== '/' && currentPath.startsWith(linkPath)) {
                        // For nested paths, check if current path starts with link path
                        link.classList.add('tabbed-header__nav-link--selected');
                        link.setAttribute('aria-current', 'page');
                    }
                } catch (e) {
                    // Ignore invalid URLs
                    console.warn('Invalid URL in tabbed header:', link.href);
                }
            });
        }

        /**
         * Sets up event listeners for navigation and URL changes
         */
        setupEventListeners() {
            // Listen for popstate (back/forward navigation)
            if (!this.popstateListener) {
                this.popstateListener = () => {
                    this.currentUrl = window.location.pathname;
                    this.updateActiveStates();
                    requestAnimationFrame(() => {
                        this.scrollToActiveTab();
                    });
                };
                window.addEventListener('popstate', this.popstateListener);
            }

            // Poll for URL changes (preload-links uses replaceState which doesn't fire popstate)
            if (!this.urlCheckInterval) {
                this.urlCheckInterval = setInterval(() => {
                    if (window.location.pathname !== this.currentUrl) {
                        this.currentUrl = window.location.pathname;
                        this.updateActiveStates();
                        requestAnimationFrame(() => {
                            this.scrollToActiveTab();
                        });
                    }
                }, 100);
            }

            // Listen for clicks to update immediately
            this.navLinks.forEach((link) => {
                if (!link.dataset.tabbedHeaderListener) {
                    link.dataset.tabbedHeaderListener = 'true';
                    link.addEventListener('click', () => {
                        // Small delay to allow preload-links to update the URL
                        setTimeout(() => {
                            this.currentUrl = window.location.pathname;
                            this.updateActiveStates();
                            requestAnimationFrame(() => {
                                this.scrollToActiveTab();
                            });
                        }, 150);
                    });
                }
            });
        }

        /**
         * Scrolls to active tab on mobile
         */
        scrollToActiveTab() {
            if (!this.navList) return;

            const activeLink = this.querySelector('.tabbed-header__nav-link--selected');
            if (!activeLink) return;

            // Check if we're on mobile (viewport width <= 640px)
            if (window.innerWidth <= 640) {
                const navListRect = this.navList.getBoundingClientRect();
                const linkRect = activeLink.getBoundingClientRect();

                // Calculate scroll position to center the active link
                const scrollLeft = this.navList.scrollLeft;
                const linkLeft = linkRect.left - navListRect.left + scrollLeft;
                const linkWidth = linkRect.width;
                const navListWidth = navListRect.width;

                const targetScroll = Math.max(0, linkLeft - (navListWidth / 2) + (linkWidth / 2));

                this.navList.scrollTo({
                    left: targetScroll,
                    behavior: 'smooth'
                });
            }
        }

        disconnectedCallback() {
            // Clean up listeners
            if (this.popstateListener) {
                window.removeEventListener('popstate', this.popstateListener);
                this.popstateListener = null;
            }
            if (this.urlCheckInterval) {
                clearInterval(this.urlCheckInterval);
                this.urlCheckInterval = null;
            }
        }
    }

    customElements.define('tabbed-header', TabbedHeader);
}

